import { useEffect, useRef, useState } from 'react';
import { ErrorDocument, ErrorStatus, SERVICE_DISPLAY_NAMES } from '@/types';
import { getSeverityDisplay } from '@/services/statusCalculator';
import { updateErrorStatus } from '@/services/firebase';
import { useAdminAuth } from '@/hooks';
import { ErrorDetails } from './ErrorDetails';
import { RelativeTime } from './RelativeTime';

interface ErrorItemProps {
  error: ErrorDocument;
  defaultExpanded?: boolean;
}

const STATUS_CONFIG: Record<ErrorStatus, { label: string; bgColor: string; color: string }> = {
  open: { label: 'Open', bgColor: 'bg-red-50', color: 'text-red-700' },
  investigating: { label: 'Investigating', bgColor: 'bg-amber-50', color: 'text-amber-700' },
  resolved: { label: 'Resolved', bgColor: 'bg-emerald-50', color: 'text-emerald-700' },
};

const STATUS_DOTS: Record<ErrorStatus, string> = {
  open: 'bg-red-500',
  investigating: 'bg-amber-500',
  resolved: 'bg-emerald-500',
};

/**
 * One error, as an incident-history row.
 *
 * Layout follows cloudflarestatus.com's incident list: severity chip, where it
 * happened, state, then the timestamp pinned right. The message is the only
 * full-width element, because it is the only part that varies in length.
 *
 * Auth is delegated to useAdminAuth rather than repeated here — this component
 * used to own a second copy of the sign-in flow, which is how the only sign-in
 * control on the page ended up inside a row that a signed-out visitor could
 * never see.
 */
export function ErrorItem({ error, defaultExpanded = false }: ErrorItemProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [toast, setToast] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, isAdmin, authError, signIn } = useAdminAuth();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Surface the hook's auth error briefly, then get out of the way.
  useEffect(() => {
    if (!authError) return;
    setToast(authError);
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [authError]);

  const severityDisplay = getSeverityDisplay(error.severity);
  // `lastSeen`, not `timestamp`: `timestamp` is frozen at first sighting, so a
  // recurring error that fired a minute ago would otherwise be stamped with a
  // date from months back. First seen is still shown in the expanded Timeline.
  const recency = error.lastSeen?.toDate() ?? error.timestamp?.toDate();

  const currentStatus: ErrorStatus = error.status || (error.resolved ? 'resolved' : 'open');
  const statusConfig = STATUS_CONFIG[currentStatus];

  const handleBadgeClick = async () => {
    if (!user) {
      const signedIn = await signIn();
      if (signedIn) setShowDropdown(true);
      return;
    }
    if (isAdmin) setShowDropdown(!showDropdown);
  };

  const handleStatusChange = async (newStatus: ErrorStatus) => {
    if (newStatus === currentStatus) {
      setShowDropdown(false);
      return;
    }
    setIsUpdating(true);
    try {
      await updateErrorStatus(error.id, newStatus, user?.email || undefined);
    } catch (err) {
      console.error('Failed to update error status:', err);
    } finally {
      setIsUpdating(false);
      setShowDropdown(false);
    }
  };

  return (
    <div>
      <div
        className="cursor-pointer px-4 py-3.5 transition-colors hover:bg-slate-50 sm:px-5"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span
                className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${severityDisplay.bgColor} ${severityDisplay.color}`}
              >
                {severityDisplay.label}
              </span>
              <span className="text-xs text-slate-500">
                {SERVICE_DISPLAY_NAMES[error.service] || error.service}
              </span>
              <span className="text-xs text-slate-400">{error.source}</span>

              <div className="relative" ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={handleBadgeClick}
                  disabled={isUpdating}
                  className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium transition-opacity hover:opacity-80 disabled:opacity-50 ${statusConfig.bgColor} ${statusConfig.color}`}
                  title={isAdmin ? 'Click to change status' : 'Click to sign in as admin'}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOTS[currentStatus]}`} aria-hidden />
                  {isUpdating ? '…' : statusConfig.label}
                </button>
                {showDropdown && isAdmin && (
                  <div className="absolute left-0 top-full z-10 mt-1 min-w-[132px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                    {(Object.keys(STATUS_CONFIG) as ErrorStatus[]).map((status) => (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(status)}
                        className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-slate-50 ${
                          status === currentStatus ? 'font-medium' : ''
                        }`}
                      >
                        <span className={`h-2 w-2 rounded-full ${STATUS_DOTS[status]}`} aria-hidden />
                        {STATUS_CONFIG[status].label}
                      </button>
                    ))}
                  </div>
                )}
                {toast && (
                  <div className="absolute left-0 top-full z-10 mt-1 whitespace-nowrap rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-600">
                    {toast}
                  </div>
                )}
              </div>
            </div>

            <p className={`text-sm text-slate-900 ${isExpanded ? '' : 'truncate'}`}>
              {error.message}
            </p>
            {error.errorCode && !isExpanded && (
              <p className="mt-1 text-xs text-slate-500">Code: {error.errorCode}</p>
            )}
          </div>

          <div className="flex shrink-0 items-start gap-2.5">
            <div className="text-right">
              {recency && (
                <RelativeTime date={recency} className="block text-xs text-slate-500" />
              )}
              {error.count > 1 && (
                <p className="mt-0.5 text-xs font-medium text-slate-600">
                  {error.count.toLocaleString()}×
                </p>
              )}
            </div>
            <svg
              className={`mt-0.5 h-4 w-4 text-slate-400 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {isExpanded && <ErrorDetails error={error} />}
    </div>
  );
}
